import { defaultAction } from "./defaultAction";
const FILL_BUY_RECEIPT_INFO = "FILL_BUY_RECEIPT_INFO";
import { sendBugsnagError } from "@utils/Bugsnag";
import {
  RESET_COUPON,
  UPDATE_NUMBER_OF_CARDS_IN_BUY,
  SET_BUY_CART_ID,
  ADD_BUY_CARD,
  RESET_BUY,
  CART_BUY_SHOW_POPUP_CART,
  CART_BUY_SHOW_LOADING,
  CART_BUY_HIDE_POPUP_CART,
  ADD_CARD_TO_BUY_CART,
  ENABLE_COUPON,
  OPEN_ERROR_BOX,
  HIDE_LOADING,
  SHOW_LOADING,
  SHOW_ERROR_ORDER_DETAILS,
  DELETE_BUY_CARD,
  SET_BUY_ORDER_UID,
  UPDATE_BUY_CART_ERROR_MSG,
  RESET_BUY_CART_COUPON,
  BUY_ORDERS_THUNK_RUNNING,
  FILL_BUY_ORDERS,
  FILL_BUY_ORDER,
  UPDATE_COUPON_ENTER
} from "@constants/actionTypes";
import { getBuyCards } from "@utils/validators/card";
import { getMerchInventory } from "./inventory";
import { removeEmpty } from "@utils";
import { errorResponseMessage } from "./errorResponseMessage";
import { isSchemaError } from "./isSchemaError";
import { getFormValues } from "redux-form";
import { getMainCheckoutAddress } from "./getMainCheckoutAddress";
import { updateCustomerPhoneAndAddress } from "./customerInfo";
import { getCustomerProfile } from "./getCustomerProfile";

const LOAD_BUY_RECEIPT = "LOAD_BUY_RECEIPT";
const FIND_TRACKING_DETAILS = "FIND_TRACKING_DETAILS";

export const getBuyReceiptInfo = orderId => async (dispatch, getState, api) => {
  try {
    const results = await api.get(`v3/orders/${orderId}`);
    dispatch(defaultAction(FILL_BUY_RECEIPT_INFO, results.data.buyOrder));
  } catch (error) {
    sendBugsnagError(error, "actions/getBuyReceiptInfo");
    dispatch(defaultAction(SHOW_ERROR_ORDER_DETAILS, "SHOW_ERROR"));
  }
};

export const createBuyCart =
  (merchID, qty, currentSort, type, discount, actVal) =>
  async (dispatch, getState, api) => {
    try {
      const buyCart = await api.post("v3/carts", { action: "buy" });
      dispatch(defaultAction(SET_BUY_CART_ID, buyCart.data.cartId));
      dispatch(
        sendCardToBuyCart(
          buyCart.data.cartId,
          merchID,
          qty,
          currentSort,
          type,
          discount,
          actVal
        )
      );
    } catch (error) {
      sendBugsnagError(error, "actions/createBuyCart");
    }
  };

export const sendCardToBuyCart = (
  cartId,
  merchID,
  qty,
  currentSort,
  type,
  percentOff,
  faceValue
) => {
  return sendCardsToBuyCart([
    {
      cartId,
      merchID,
      qty,
      currentSort,
      type,
      percentOff,
      faceValue
    }
  ]);
};

export const sendCardsToBuyCart =
  (skus, attempts = 1) =>
  async (dispatch, getState, api) => {
    const { cardsBuy } = getState();
    const { cartId, merchID, currentSort, faceValue, percentOff, type } =
      skus[0];

    dispatch(
      defaultAction(CART_BUY_SHOW_LOADING, {
        value: faceValue,
        percent: percentOff,
        merchId: merchID,
        state: true,
        type: type
      })
    );
    try {
      const cardsList = skus.map(sku => {
        return {
          merchantId: Number(merchID),
          quantity: Number(sku.qty),
          percentOff: Number(sku.percentOff),
          faceValue: Number(sku.faceValue),
          type: sku.type
        };
      });
      const results = await api.post(`v3.1/carts/${cartId}/cards`, {
        cards: cardsList
      });
      dispatch(defaultAction(CART_BUY_SHOW_POPUP_CART));
      dispatch(
        defaultAction(CART_BUY_SHOW_LOADING, {
          value: faceValue,
          percent: percentOff,
          merchId: merchID,
          state: false,
          type: type
        })
      );
      const buyCards = getBuyCards(results.data.cart, cardsBuy);
      const allCards = buyCards[1];
      const cart = buyCards[0];
      dispatch(defaultAction(ADD_BUY_CARD, cart));
      dispatch(getMerchInventory(merchID, currentSort));
      let qty = 0;
      Object.keys(cart).forEach(card => {
        qty += cart[card].quantity;
      });
      dispatch(defaultAction(UPDATE_NUMBER_OF_CARDS_IN_BUY, qty));
      const couponData = results.data.cart.couponData;
      if (couponData) {
        if (couponData.errorMsg) {
          await api.delete(
            `v3/carts/${results.data.cart.cartId}/coupons/${couponData.couponCode}`
          );
          dispatch(defaultAction(UPDATE_COUPON_ENTER, couponData.couponCode));
        } else {
          dispatch(defaultAction(UPDATE_COUPON_ENTER, couponData.coupon));
        }

        dispatch(defaultAction(ENABLE_COUPON, couponData));
      }

      await dispatch(defaultAction(ADD_CARD_TO_BUY_CART, allCards));
      if (results.data.added < results.data.requested) {
        dispatch(
          defaultAction(OPEN_ERROR_BOX, {
            title: "Sorry",
            message:
              "Some of the selected cards are no longer available and have not been added to your cart."
          })
        );
      }
      dispatch(
        defaultAction(CART_BUY_SHOW_LOADING, {
          value: faceValue,
          percent: percentOff,
          merchId: merchID,
          state: false,
          type: type
        })
      );
    } catch (error) {
      let errMsg = errorResponseMessage(error);
      if (errMsg) {
        if (errMsg.includes("insufficient inventory")) {
          errMsg = "That card is no longer available.";
          dispatch(getMerchInventory(merchID, currentSort));
        }
      } else {
        errMsg = "Something went wrong, please refresh your page.";
      }
      if (attempts > 0 && errMsg.includes("cart not found")) {
        sendBugsnagError(error, "actions/sendCardsToBuyCart/attemptingRetry");
        await dispatch(defaultAction(RESET_BUY, null));
        const buyCart = await api.post("v3/carts", { action: "buy" });
        await dispatch(defaultAction(SET_BUY_CART_ID, buyCart.data.cartId));
        await dispatch(sendCardsToBuyCart(skus, 0));
      } else {
        dispatch(defaultAction(CART_BUY_HIDE_POPUP_CART));
        dispatch(
          defaultAction(OPEN_ERROR_BOX, { title: "Sorry", message: errMsg })
        );
        dispatch(
          defaultAction(CART_BUY_SHOW_LOADING, {
            value: faceValue,
            percent: percentOff,
            merchId: merchID,
            state: false,
            type: type
          })
        );
        sendBugsnagError(error, "actions/sendCardsToBuyCart");
      }
    }
  };

export const adjustCardQuantityToCart =
  (cartId, cardId, qty, loadingAction, updateLoadingMessage) =>
  async (dispatch, getState, api) => {
    try {
      const { cardsBuy, cartBuy } = getState();
      dispatch(defaultAction(loadingAction, { status: true, cardId }));
      const results = await api.put(`v3/carts/${cartId}/cards/${cardId}`, {
        qty: Number(qty)
      });
      const couponData = results.data.couponData;
      const buyCards = getBuyCards(results.data, cardsBuy);
      const allCards = buyCards[1];
      const cart = buyCards[0];
      dispatch(defaultAction(ADD_BUY_CARD, cart));
      let cardQuantity = 0;
      Object.keys(cart).forEach(card => {
        cardQuantity += cart[card].quantity;
      });
      dispatch(defaultAction(UPDATE_NUMBER_OF_CARDS_IN_BUY, cardQuantity));

      if (couponData) {
        if (couponData.errorMsg) {
          await api.delete(
            `v3/carts/${cartBuy.cartId}/coupons/${couponData.couponCode}`
          );
          dispatch(defaultAction(UPDATE_COUPON_ENTER, couponData.couponCode));
        } else {
          dispatch(defaultAction(UPDATE_COUPON_ENTER, couponData.coupon));
        }
        dispatch(defaultAction(ENABLE_COUPON, couponData));
      }

      dispatch(defaultAction(ADD_CARD_TO_BUY_CART, allCards));
      if (cartBuy.merchantPageSlug) {
        dispatch(
          getMerchInventory(
            getState().cartBuy.merchantPageSlug,
            "FILL_INVENTORY"
          )
        );
      }
    } catch (error) {
      sendBugsnagError(error, "actions/adjustCardQuantityToCart");
      dispatch(defaultAction(loadingAction, { status: true, cardId }));
      let errMsg = errorResponseMessage(error);
      if (errMsg) {
        if (errMsg.includes("insufficient inventory")) {
          errMsg = "Sorry we don't have more cards like this.";
        }
      } else {
        errMsg = "Something went wrong, please refresh your page.";
      }
      dispatch(defaultAction(updateLoadingMessage, { errMsg, cardId }));
    } finally {
      dispatch(defaultAction(loadingAction, { status: false, cardId }));
    }
  };

export const deleteCardInCart =
  (cartSellID, cardID) => async (dispatch, getState, api) => {
    try {
      const { cardsBuy } = getState();
      const results = await api.delete(
        `v3/carts/${cartSellID}/cards/${cardID}`
      );
      const couponData = results.data.couponData;
      const { cartBuy } = getState();
      dispatch(defaultAction(DELETE_BUY_CARD, cardID));
      dispatch(
        defaultAction(
          UPDATE_NUMBER_OF_CARDS_IN_BUY,
          Object.keys(cardsBuy).length
        )
      );
      if (couponData) {
        if (couponData.errorMsg) {
          await api.delete(
            `v3/carts/${cartBuy.cartId}/coupons/${couponData.couponCode}`
          );
          dispatch(defaultAction(UPDATE_COUPON_ENTER, couponData.couponCode));
        } else {
          dispatch(defaultAction(UPDATE_COUPON_ENTER, couponData.coupon));
        }
        dispatch(defaultAction(ENABLE_COUPON, couponData));
      } else {
      }

      if (cartBuy.merchantPageSlug) {
        dispatch(
          getMerchInventory(
            getState().cartBuy.merchantPageSlug,
            "FILL_INVENTORY"
          )
        );
      }
    } catch (error) {
      sendBugsnagError(error, "actions/deleteCardInCart");
      dispatch(
        defaultAction(OPEN_ERROR_BOX, {
          title: "Sorry, something went wrong.",
          message:
            "We are sorry. Please refresh the page and try again. Thank you."
        })
      );
    }
  };

export const createBuyOrder =
  (orderObj, history) => async (dispatch, getState, api) => {
    dispatch(defaultAction(SHOW_LOADING));
    let results;

    try {
      const { cartBuy } = getState();
      results = await api.post("v3/orders", { cartId: cartBuy.cartId });
      dispatch(defaultAction(SET_BUY_ORDER_UID, results.data.orderId));
    } catch (error) {
      dispatch(defaultAction(HIDE_LOADING));
      sendBugsnagError(error, "actions/createBuyOrder/results");
      let errorMessage =
        errorResponseMessage(error) ||
        "We are unable to complete your order please refresh the page and try again.";
      if (isSchemaError(errorMessage)) {
        errorMessage =
          "We are unable to complete your order please refresh the page and try again.";
      }
      dispatch(defaultAction(UPDATE_BUY_CART_ERROR_MSG, errorMessage));
      return;
    }

    await dispatch(updateBuyOrder(results.data.orderId, orderObj, history));
  };

export const updateBuyOrder =
  (orderId, orderObj, history) => async (dispatch, getState, api) => {
    const state = getState();
    try {
      const { customerInfo, userActions } = state;
      dispatch(defaultAction(SHOW_LOADING));

      const mainCheckoutBuy = getFormValues("mainCheckoutBuy")(state);

      if (customerInfo.missingInfo) {
        let phone =
          mainCheckoutBuy.billingAddress && mainCheckoutBuy.phone.phone;
        let address = {};
        const formShippingAddress = await dispatch(
          getMainCheckoutAddress("shippingAddress")
        );

        const formBillingAddress = await dispatch(
          getMainCheckoutAddress("billingAddress")
        );

        if (mainCheckoutBuy.billingAddress.onlyFormDisplayed) {
          address = formBillingAddress;
        } else {
          if (mainCheckoutBuy.billingAddress.sameAsShipping) {
            address = formShippingAddress;
          } else {
            address = formBillingAddress;
          }
        }
        removeEmpty(address);

        await dispatch(updateCustomerPhoneAndAddress(phone, address));
        await dispatch(getCustomerProfile());
      }
      const results = await api.put(`v3/orders/${orderId}`, orderObj);
      const { editShipping, cardsBuy, coupon } = state;
      const receiptInfo = {
        editShipping,
        customerInfo,
        cardsBuy,
        userActions,
        coupon,
        orderId: results.data.orderId
      };
      dispatch(findTrackingDetails(orderId));
      dispatch(defaultAction(LOAD_BUY_RECEIPT, receiptInfo));
      dispatch(defaultAction(RESET_COUPON));
      dispatch(defaultAction(RESET_BUY_CART_COUPON));
      setTimeout(() => {
        history.push("/receipt/");
        dispatch(defaultAction(HIDE_LOADING));
      }, 1000);
    } catch (error) {
      sendBugsnagError(error, "actions/updateBuyOrder");
      let errorMessage =
        errorResponseMessage(error) ||
        "We are unable to complete your order please refresh the page and try again.";
      if (isSchemaError(errorMessage)) {
        errorMessage =
          "We are unable to complete your order please refresh the page and try again.";
      }
      dispatch(defaultAction(UPDATE_BUY_CART_ERROR_MSG, errorMessage));
      dispatch(defaultAction(HIDE_LOADING));
    }
  };

export const findTrackingDetails = id => async (dispatch, getState, api) => {
  try {
    const results = await api.get(`v3/orders/${id}`);
    dispatch(
      defaultAction(FIND_TRACKING_DETAILS, results.data.buyOrder.trackingData)
    );
  } catch (error) {
    sendBugsnagError(error, "actions/findTrackingDetails");
  }
};

export const getBuyOrders = () => async (dispatch, getState, api) => {
  dispatch(defaultAction(BUY_ORDERS_THUNK_RUNNING, true));

  try {
    const results = await api.get("v3/orders/buy");
    const sorted = results?.data?.sort(
      (a, b) => new Date(b.datePurchased) - new Date(a.datePurchased)
    );
    dispatch(defaultAction(FILL_BUY_ORDERS, sorted || []));
    dispatch(defaultAction(BUY_ORDERS_THUNK_RUNNING, false));
    return sorted;
  } catch (error) {
    sendBugsnagError(error, "actions/getBuyOrders");
    dispatch(defaultAction(BUY_ORDERS_THUNK_RUNNING, false));
  }
};

export const getBuyOrder = id => async (dispatch, getState, api) => {
  try {
    const results = await api.get(`v3/orders/${id}`);
    const buyOrder = {
      ...results.data.buyOrder,
      paymentDetails: {
        ...results.data.buyOrder.paymentDetails,
        method:
          results.data.buyOrder.paymentDetails.method === "Bitcoin"
            ? "Crypto"
            : results.data.buyOrder.paymentDetails.method
      }
    };
    await dispatch(defaultAction(FILL_BUY_ORDER, buyOrder));
  } catch (error) {
    sendBugsnagError(error, "actions/getBuyOrder");
  }
};

export const getFormCheckoutValues = () => async (dispatch, getState) => {
  return getFormValues("mainCheckoutBuy")(getState());
};

export const getSyncedErrors = () => async (dispatch, getState) => {
  return getState().form.mainCheckoutBuy?.syncErrors;
};

export const createBuyOrderPost = () => async (dispatch, getState, api) => {
  try {
    const { cartBuy } = getState();
    const orderPostResults = await api.post("v3/orders", {
      cartId: cartBuy.cartId
    });

    dispatch(defaultAction(SET_BUY_ORDER_UID, orderPostResults.data.orderId));
    return orderPostResults.data.orderId;
  } catch (error) {
    sendBugsnagError(error, "actions/createBuyOrderPost");
  }
};

export const initializeZipPay =
  (body, orderId) => async (dispatch, getState, api) => {
    try {
      const results = await api.post(
        `v3/orders/${orderId}/payments/zip/signature`,
        {
          body: body
        }
      );

      return results.data;
    } catch (error) {
      console.log("error: ", error);
      dispatch(defaultAction(HIDE_LOADING));
      sendBugsnagError(error, "actions/initializeZipPay");
    }
  };

export const updateOrderWithZipPaySuccess =
  (body, history) => async (dispatch, getState, api) => {
    const { customerInfo, editShipping, userActions, cardsBuy, coupon } =
      getState();

    let hasRetried = false;

    const processOrder = async () => {
      try {
        const results = await api.put(
          `v3/orders/${customerInfo.orderUIDBuy}`,
          body
        );
        const orderId = results.data.orderId;

        const receiptInfo = {
          editShipping,
          customerInfo,
          cardsBuy,
          userActions,
          coupon,
          orderId
        };

        dispatch(findTrackingDetails(orderId));
        dispatch(defaultAction(LOAD_BUY_RECEIPT, receiptInfo));
        dispatch(defaultAction(RESET_COUPON));
        dispatch(defaultAction(RESET_BUY_CART_COUPON));

        setTimeout(() => history.push("/receipt/"), 10);
        return true;
      } catch (error) {
        console.error("error: ", error);

        // Only retry once
        if (!hasRetried) {
          hasRetried = true;
          return false;
        }

        sendBugsnagError(error, "actions/updateOrderWithZipPaySuccess");
        return true;
      }
    };

    try {
      const success = await processOrder();
      if (!success) {
        await processOrder();
      }
    } finally {
      dispatch(defaultAction(HIDE_LOADING));
    }
  };
